import React from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';

/**
 * InputField Component
 * Reusable input field with validation, icons, and consistent styling
 *
 * @param {string} label - Label text for the input
 * @param {string} type - Input type (text, email, tel, etc.)
 * @param {string} value - Current input value
 * @param {function} onChange - Change handler
 * @param {function} onBlur - Blur handler for validation
 * @param {string} error - Error message to display
 * @param {string} helperText - Helper text below input
 * @param {boolean} success - Show success state
 * @param {React.Component} icon - Icon component to display (left side)
 * @param {boolean} required - Mark field as required
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Disable input
 * @param {string} id - Input ID
 * @param {string} name - Input name
 * @param {string} autoComplete - Autocomplete attribute
 */
export default function InputField({
  label,
  type = 'text',
  value,
  onChange,
  onBlur,
  error,
  helperText,
  success = false,
  icon: Icon,
  required = false,
  placeholder,
  disabled = false,
  id,
  name,
  autoComplete,
  className = ''
}) {
  const inputId = id || name || label?.toLowerCase().replace(/\s+/g, '-');
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;

  const handleFocus = () => {};
  const handleBlur = (e) => {
    if (onBlur) onBlur(e);
  };

  // Determine input state classes
  let inputStateClasses = 'border-[var(--border-subtle)] focus:ring-[var(--focus-ring)] focus:border-[var(--brand-terracotta)]';
  if (error) {
    inputStateClasses = 'border-[var(--status-danger)] focus:ring-[var(--status-danger)] focus:border-[var(--status-danger)]';
  } else if (success && value) {
    inputStateClasses = 'border-[var(--status-success)] focus:ring-[var(--status-success)] focus:border-[var(--status-success)]';
  }

  const inputClasses = `
    w-full px-4 py-3 rounded-xl border-2
    ${Icon ? 'pl-12' : ''}
    ${error || success ? 'pr-12' : ''}
    text-[var(--text-primary)] placeholder-[var(--text-muted)]
    transition-all duration-200
    focus:outline-none focus:ring-2
    ${inputStateClasses}
    ${disabled ? 'bg-[var(--bg-tertiary)] cursor-not-allowed opacity-60' : 'bg-[var(--surface-card)]'}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className={`block text-sm font-semibold text-[var(--text-secondary)] mb-2 ${required ? 'label-required' : ''}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        {/* Left Icon */}
        {Icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">
            <Icon size={20} />
          </div>
        )}

        {/* Input */}
        <input
          id={inputId}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          className={inputClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? errorId : helperText ? helperId : undefined
          }
          aria-required={required ? 'true' : 'false'}
        />

        {/* Right Status Icon */}
        {error && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-red-500">
            <AlertCircle size={20} />
          </div>
        )}
        {success && value && !error && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500">
            <CheckCircle size={20} />
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div
          id={errorId}
          role="alert"
          className="mt-1 text-xs text-red-600 flex items-center gap-1"
        >
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {/* Helper Text */}
      {helperText && !error && (
        <div id={helperId} className="mt-1 text-xs text-[var(--text-muted)]">
          {helperText}
        </div>
      )}
    </div>
  );
}
